import React, { useState, useCallback, useEffect } from "react";

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
  MenuItem,
  TextField as MuiTextField,
  Typography,
} from "@material-ui/core";
import { useDropzone } from "react-dropzone";
import {
  ArrayField,
  Button,
  Datagrid,
  FunctionField,
  TextField,
  useNotify,
} from "react-admin";

import {
  AddAllIcon,
  AttachFileIcon,
  CancelIcon,
  CloseIcon,
} from "../../../components/common/iconography";
import { MAX_FILE_SIZE, MAX_FILE_SIZE_MSG } from "../../../utils/constants";
import { logError } from "../../../utils/appUtils";
import { csv2arr } from "../../../utils/fileUtils";
import { fetchAccounts, isSameAccount, isSameCustomer } from "./AccountUtils";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(0),
    justifyContent: "space-between",
  },
  title: {
    paddingTop: theme.spacing(2),
  },
  dialogContent: {
    // https://stackoverflow.com/questions/47698037/how-can-i-set-a-height-to-a-dialog-in-material-ui
    height: "90vh",
  },

  dropZone: {
    background: theme.palette.background.default,
    cursor: "pointer",
    padding: theme.spacing(2),
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    textAlign: "center",
    color: theme.palette.text.secondary,
    transition: "background 0.2s ease-in-out",
    "&:hover": {
      background: theme.palette.action.hover,
    },
  },
  activeDropZone: {
    background: theme.palette.action.selected,
  },
  caption: {
    marginTop: theme.spacing(1),
  },

  fakeFieldset: {
    position: "relative",
    border: "1px solid #ccc",
    borderRadius: 4,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  fakeLegend: {
    position: "absolute",
    top: -theme.spacing(2),
    left: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    padding: "0 4px",
  },
  scrollableGrid: {
    overflowX: "auto",
    width: "100%",
    "&::-webkit-scrollbar": {
      height: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#888",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#555",
    },
  },
}));

// Column mapping labels; "Business Name" is required to import (see validateMappedRows + IMPORT button).
const requiredFields = [
  { id: "businessName", name: "Business Name" },
  { id: "displayName", name: "Display Name" },
  { id: "lotNumberPrefix", name: "Lot Number Prefix" },
  { id: "email", name: "Email" },
  { id: "billingEmail", name: "Billing Email" },
  { id: "phone", name: "Phone" },
  { id: "fax", name: "Fax" },
  { id: "address.street", name: "Street Address" },
  { id: "address.city", name: "City" },
  { id: "address.state", name: "State" },
  { id: "address.zip", name: "Zip" },
  { id: "address.country", name: "Country" },
];

// Customer CSV validation  occurs via mapping selections during submit.
const processCSV = async (
  str,
  setLoading,
  setColumns,
  setCSVData,
  setCSVErrors,
  notify,
) => {
  try {
    debugger;
    let headers = str.slice(0, str.indexOf("\n")).replace("\r", "").split(",");
    headers = headers.filter((head) => head !== "");
    const hlen = headers.length;

    let rows = csv2arr(str.slice(str.indexOf("\n") + 1));
    rows = rows.filter((row) => row.join("") !== "");

    let lengthErrorFound = false;

    const parsedRows = rows
      .map((row, index) => {
        if (row.length > 1) {
          const obj = headers.reduce((acc, header, i) => {
            acc[header] = row[i];
            return acc;
          }, {});
          if (row.length > hlen) {
            obj["error"] = true;
            lengthErrorFound = true;
          }
          obj.rowIndex = index;
          return obj;
        }
        return null;
      })
      .filter(Boolean);

    const lengthErrors = lengthErrorFound
      ? ["Some rows have extra unquoted commas or columns."]
      : [];

    setCSVErrors(lengthErrors);

    setCSVData(parsedRows);
    const columns = headers.map((h) => ({ id: h, name: h }));
    setColumns([{ id: null, name: <span>&nbsp;</span> }, ...columns]);
  } catch (err) {
    logError("accounts:AccountUpload:processCSV", err, notify);
  } finally {
    setLoading(false);
  }
};

export const AccountImportDialog = ({
  total,
  isOpen,
  setIsOpen,
  handleImport,
  ...props
}) => {
  const classes = useStyles();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [csvData, setCSVData] = useState([]);
  const [csvErrors, setCSVErrors] = useState([]);
  const [customersFile, setCustomersFile] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [matchedAccounts, setMatchedAccounts] = useState([]);
  const [fieldMap, setFieldMap] = useState({});

  useEffect(() => {
    if (total > 0) fetchAccounts(notify, setAccounts, setLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  // Validate mapped rows
  const validateMappedRows = (rows, map) => {
    const errors = [];
    if (!map?.businessName) {
      errors.push('"Business Name" mapping is required.');
      return errors;
    }

    const seen = [];

    rows.forEach((row, index) => {
      const companyCol = map?.businessName;
      const company = (companyCol ? row?.[companyCol] : "")?.trim?.() || "";

      if (!company) {
        errors.push(`Row ${index + 1}: missing "Business Name".`);
      }

      const nameCol = map?.displayName;
      const displayVal = (nameCol ? row?.[nameCol] : "")?.trim?.() || "";

      const normalized = {
        // Provide keys expected by isSameCustomer for robust duplicate checks
        "Customer name": company, // mirror company into customer name
        "Company name": company,
        Name: displayVal || company,
      };

      const isDuplicate = seen.some((existing) =>
        isSameCustomer(existing, normalized),
      );
      if (isDuplicate) {
        errors.push(
          `Row ${index + 1}: appears to be a duplicate of a previous row.`,
        );
      } else {
        seen.push(normalized);
      }
    });

    return errors;
  };

  const handleSubmit = () => {
    const rowErrors = validateMappedRows(csvData, fieldMap);
    if (rowErrors.length > 0) {
      setCSVErrors([...(csvErrors || []), ...rowErrors]);
      return;
    }

    const getMappedValue = (row, mapKey) => {
      const col = fieldMap?.[mapKey];
      return col ? row[col] : undefined;
    };

    const toNormalizedRow = (row) => ({
      // populate standard keys used by downstream logic
      "Company name": getMappedValue(row, "businessName"),
      Name:
        getMappedValue(row, "displayName") ||
        getMappedValue(row, "businessName"),
      Email: getMappedValue(row, "email"),
      Phone: getMappedValue(row, "phone"),
      "Street Address": getMappedValue(row, "address.street"),
      City: getMappedValue(row, "address.city"),
      State: getMappedValue(row, "address.state"),
      Zip: getMappedValue(row, "address.zip"),
      Country: getMappedValue(row, "address.country"),
    });

    const mergedData = csvData.map((row, i) => {
      const normalized = toNormalizedRow(row);
      const mappedBusinessName =
        getMappedValue(row, "businessName")?.toString().trim() || undefined;
      const mappedDisplayName =
        getMappedValue(row, "displayName")?.toString().trim() || undefined;

      const accountId = matchedAccounts[i];
      if (accountId) {
        const existingAccount = accounts.find((a) => a.id === accountId);
        return {
          ...existingAccount,
          businessName: mappedBusinessName || existingAccount?.businessName,
          displayName:
            mappedDisplayName ?? existingAccount?.displayName ?? undefined,
          billingEmail:
            normalized["Email"] || existingAccount?.billingEmail || undefined,
          phone: normalized["Phone"] || existingAccount?.phone || undefined,
          address: {
            name: existingAccount?.address?.name || "",
            street:
              normalized["Street Address"] ||
              existingAccount?.address?.street ||
              "",
            city: normalized["City"] || existingAccount?.address?.city || "",
            state: normalized["State"] || existingAccount?.address?.state || "",
            zipCode:
              normalized["Zip"] || existingAccount?.address?.zipCode || "",
            country:
              normalized["Country"] || existingAccount?.address?.country || "",
          },
        };
      }

      return {
        ...normalized,
        businessName: mappedBusinessName,
        ...(mappedDisplayName ? { displayName: mappedDisplayName } : {}),
      };
    });

    handleImport(mergedData);
    handleClose();
  };

  const handleClose = () => {
    debugger;
    setAccounts({});
    setCSVData([]);
    setCSVErrors([]);
    setIsOpen(false);
  };

  return (
    <Dialog maxWidth="xl" open={isOpen} fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography className={classes.title}>Accounts Upload</Typography>
        <IconButton aria-label="close" onClick={handleClose}>
          <CloseIcon fontSize="large" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <AccountImportCard
          {...props}
          classes={classes}
          setLoading={setLoading}
          csvData={csvData}
          setCSVData={setCSVData}
          csvErrors={csvErrors}
          setCSVErrors={setCSVErrors}
          accounts={accounts}
          customersFile={customersFile}
          setCustomersFile={setCustomersFile}
          matchedAccounts={matchedAccounts}
          setMatchedAccounts={setMatchedAccounts}
          fieldMap={fieldMap}
          setFieldMap={setFieldMap}
        />
      </DialogContent>
      <DialogActions>
        <Button label="Cancel" color="defalut" autoFocus onClick={handleClose}>
          <CancelIcon />
        </Button>
        <Button
          label="IMPORT"
          color="primary"
          disabled={
            loading ||
            csvData.length === 0 ||
            csvErrors?.length > 0 ||
            !fieldMap?.businessName
          }
          onClick={handleSubmit}
        >
          <AddAllIcon />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AccountImportCard = ({
  accounts,
  customersFile,
  setCustomersFile,
  setLoading,
  csvData,
  setCSVData,
  csvErrors,
  setCSVErrors,
  notify,
  classes,
  matchedAccounts,
  setMatchedAccounts,
  fieldMap,
  setFieldMap,
}) => {
  const [columns, setColumns] = useState([]);

  useEffect(() => {
    const usedIds = new Set();

    const getMappedValue = (row, key) => {
      const col = fieldMap?.[key];
      return col ? row[col] : undefined;
    };

    const toNormalizedRow = (row) => ({
      "Company name": getMappedValue(row, "businessName"),
      Name:
        getMappedValue(row, "displayName") ||
        getMappedValue(row, "businessName"),
    });

    const preMatched = csvData.map((row) => {
      const norm = toNormalizedRow(row);
      const match = accounts.find((acc) => {
        if (usedIds.has(acc.id)) return false;
        const isMatch = isSameAccount(acc, norm);
        if (isMatch) usedIds.add(acc.id);
        return isMatch;
      });
      return match?.id || "";
    });

    setMatchedAccounts(preMatched);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvData, accounts, fieldMap]);

  const handleMatchChange = (accountId, rowIndex) => {
    setMatchedAccounts((prev) => {
      const updated = [...prev];
      updated[rowIndex] = accountId;

      // remove duplicates (optional, for safety)
      const deduped = updated.map((id, idx) =>
        updated.indexOf(id) === idx ? id : "",
      );
      return deduped;
    });
  };

  const handleCSVFile = async (file) => {
    try {
      debugger;
      setLoading(true);
      setCSVErrors([]);
      if (!file) {
        setCustomersFile(null);
        setCSVData([]);
      } else if (file.size > MAX_FILE_SIZE) {
        notify(MAX_FILE_SIZE_MSG, "warning");
        setCustomersFile(null);
        setCSVData([]);
      } else {
        setCustomersFile(file);
        const reader = new FileReader();
        reader.onload = function (e) {
          const text = e.target.result;
          processCSV(
            text,
            setLoading,
            setColumns,
            setCSVData,
            setCSVErrors,
            notify,
          );
        };
        reader.readAsText(file);
      }
    } catch (err) {
      logError("accounts:AccountUpload:handleCSVFile", err, notify);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    debugger;
    if (acceptedFiles.length > 0) {
      handleCSVFile(acceptedFiles[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ".csv",
    multiple: false,
    onDrop,
  });

  return (
    <Grid container direction="row" spacing={2}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Typography variant="overline" gutterBottom>
            Instructions
          </Typography>
          <ol>
            <li>Export Accounts / Customers from your accounting system.</li>
            <li>
              Ensure to include the account name, display name, and customer ID
              if available.
            </li>
            <li>
              Save as a comma-separated-values (.CSV) file and upload here.
            </li>
            <li>
              If errors are found, review, correct the errors and re-upload.
            </li>
            <li>For help, contact us at 877-TRANSLOAD / 877-872-6756</li>
          </ol>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="overline" gutterBottom>
            Upload Customer CSV
          </Typography>
          <Box
            {...getRootProps()}
            className={`${classes.dropZone} ${
              isDragActive ? classes.activeDropZone : ""
            }`}
          >
            <input {...getInputProps()} />
            <AttachFileIcon fontSize="large" />
            <Typography variant="body2" color="textSecondary">
              {isDragActive
                ? "Drop your CSV file here..."
                : "Drag & drop a CSV file here, or click to select one"}
            </Typography>
          </Box>
          {customersFile?.name && (
            <Typography variant="caption" className={classes.caption}>
              Selected: {customersFile.name}
            </Typography>
          )}
        </Grid>
      </Grid>

      {csvErrors?.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="overline" color="error">
            Loading Errors:
          </Typography>
          <ul>
            {csvErrors.map((err, idx) => (
              <Typography key={idx} color="error">
                <li>{err}</li>
              </Typography>
            ))}
          </ul>
        </Grid>
      )}

      {columns.length > 0 && (
        <>
          {/* Column mapping selectors */}
          <Grid item xs={12} className={classes.fakeFieldset}>
            <div id="field-map-legend" className={classes.fakeLegend}>
              <Typography variant="overline" color="textSecondary">
                Match Spreadsheet columns to fields
              </Typography>
            </div>
            <Grid container spacing={1}>
              {requiredFields.map((field) => (
                <Grid key={field.id} item xs={12} md={2}>
                  <MuiTextField
                    select
                    id={`select_${field.id}`}
                    size="small"
                    margin="dense"
                    variant="outlined"
                    label={field.name}
                    fullWidth
                    value={fieldMap?.[field.id] ?? null}
                    onChange={(e) =>
                      setFieldMap({ ...fieldMap, [field.id]: e.target.value })
                    }
                    required={
                      field.id === "businessName" || field.id === "displayName"
                    }
                    helperText={false}
                  >
                    {columns.map((col) => (
                      <MenuItem key={`${field.id}_${col.id}`} value={col.id}>
                        {col.name}
                      </MenuItem>
                    ))}
                  </MuiTextField>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item xs={12} className={classes.fakeFieldset}>
            <div id="match-legend" className={classes.fakeLegend}>
              <Typography variant="overline" color="textSecondary">
                Match Accounts to external customers
              </Typography>
            </div>
            <div className={classes.scrollableGrid}>
              <ArrayField source="preview" record={{ preview: csvData }}>
                <Datagrid rowClick={false} bulkActionButtons={false}>
                  {/* Account Match Column */}
                  {(!csvErrors || csvErrors.length === 0) && (
                    <FunctionField
                      label="Match Account"
                      render={(record) => {
                        const rowIndex = record.rowIndex;
                        const selectedAccountId =
                          matchedAccounts[rowIndex] || "";
                        const availableOptions = accounts.filter(
                          (acc) =>
                            !matchedAccounts.includes(acc.id) ||
                            matchedAccounts[rowIndex] === acc.id,
                        );

                        return (
                          <MuiTextField
                            select
                            fullWidth
                            size="small"
                            margin="dense"
                            variant="outlined"
                            value={selectedAccountId}
                            onChange={(e) =>
                              handleMatchChange(e.target.value, rowIndex)
                            }
                            style={{ width: "12rem" }}
                          >
                            <MenuItem value="">-- Select --</MenuItem>
                            {availableOptions.map((acc) => (
                              <MenuItem key={acc.id} value={acc.id}>
                                {acc.businessName || acc.displayName}
                              </MenuItem>
                            ))}
                          </MuiTextField>
                        );
                      }}
                    />
                  )}

                  {/* Render CSV Columns */}
                  {columns
                    .filter((col) => col.id)
                    .map((col) => (
                      <TextField
                        key={col.id}
                        source={col.id}
                        label={col.name}
                      />
                    ))}
                </Datagrid>
              </ArrayField>
            </div>
          </Grid>
        </>
      )}
    </Grid>
  );
};
